import {
  faTrashCan,
  faUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import SvgWarningIcon from '~/WarningIcon';
import { JobStatusBadge } from '~/components/ui/JobStatusBadge';
import { JobStatus, Status } from '~/generated/graphql';
import { Avatar } from './Avatar';
import { IconButton } from './nucleus/Button/IconButton';

type CardSmallProps = {
  /** URL path to pass to router link */
  linkTo: string;
  /** Optional router state to pass along with navigation */
  linkState?: unknown;
  /** If true will open item to new tab */
  newTab?: boolean;
  /** If true will show hover icon to open in new tab */
  showNewTabIcon?: boolean;
  /** URL to an image file to pass to `Image` component */
  image?: string | null;
  /** If true will show placeholder if image is empty */
  deletable?: boolean;
  /** If true will display link to delete Item */
  onClick?: () => void;
  /** onClick action for deletable */
  showImage?: boolean;
  /** First row of text */
  titleText: string;
  /** Middle row of text */
  secondaryText: string | undefined;
  /** Bottom row of text */
  tertiaryText?: React.ReactNode;
  /** Hero content for the right side. Ignored if heroElement provided */
  hero?: { number?: number | string; directive: string };
  /** Hero element for more control over what's displayed on the right side */
  heroElement?: React.ReactNode;
  /** Status to display in badge */
  status?: Status | JobStatus;
  /** If true will display yellow background and warning Icon */
  warning?: boolean;
  /** If true will display active/selected styling */
  isActive?: boolean;
  /** If true will display highlighted stroke styling */
  isHighlighted?: boolean;
  /** Object representing an assigned user */
  user?: {
    name: string;
    image?: string | null;
  } | null;
};

type CardSmallStaticProps = Omit<
  CardSmallProps,
  'linkTo' | 'newTab' | 'showNewTabIcon'
> & {
  /** Optional click handler for the entire card */
  onClick?: () => void;
};

export function CardSmall({
  linkTo,
  linkState,
  newTab,
  showNewTabIcon = false,
  image,
  showImage,
  titleText,
  deletable,
  onClick,
  secondaryText,
  user,
  status,
  hero,
  warning,
  heroElement,
  isActive,
  isHighlighted,
}: CardSmallProps) {
  const handleNewTabClick = () => {
    // Prefer opening canonical list routes for known entities (assets/spaces)
    // This replicates the assets behavior and fixes nested relative URL issues
    const segments = linkTo.split('/').filter(Boolean);
    const last = segments[segments.length - 1];

    if (segments.includes('assets')) {
      window.open(`/assets/${last}`, '_blank');
      return;
    }

    if (segments.includes('spaces')) {
      window.open(`/spaces/${last}`, '_blank');
      return;
    }

    // Generic fallback: construct absolute URL, ensuring trailing slash in base
    try {
      const base = window.location.href.endsWith('/')
        ? window.location.href
        : window.location.href + '/';
      const url = new URL(linkTo, base);
      window.open(url.toString(), '_blank');
    } catch {
      window.open(linkTo, '_blank');
    }
  };
  return (
    <div className='group relative'>
      <Link
        to={linkTo}
        state={linkState}
        target={newTab ? '_blank' : '_self'}
        className={classNames(
          'mb-2 flex w-auto flex-1 items-center gap-2 truncate rounded border px-2.5 py-2 shadow-sm transition-all duration-300',
          { 'mx-3 cursor-pointer hover:shadow': linkTo },
          { 'bg-yellow-10': warning },
          { 'bg-white': !warning && !isActive },
          { 'pointer-events-none': !linkTo },
          'mx-3 mb-2 flex items-center gap-2 rounded px-2.5 shadow-sm hover:shadow',
          // Active state styling
          {
            'border-brand bg-brand-ghost shadow-md': isActive,
            'border-grey-10': !isActive && !isHighlighted,
            'border-blue-500 ring-blue-200 border-2 ring-2': isHighlighted,
          }
        )}
      >
        {showImage && (
          <div className='h-11 w-11'>
            <Avatar
              image={image}
              name={titleText}
              size='full'
              square
              className='mr-3 h-11 w-11 rounded-md'
            />
          </div>
        )}
        <div className='ml-1 flex flex-col items-start overflow-hidden text-base '>
          <div className='flex items-center gap-1'>
            <p
              className={classNames(
                '... max-w-full truncate text-base font-medium text-grey-70 lg:text-[15px] lg:leading-5'
              )}
            >
              {titleText}
            </p>
            {status === 'Inactive' && (
              <div
                className='h-2 w-2 flex-shrink-0 rounded-full bg-red-500'
                title='Inactive'
                aria-label='Inactive status'
              />
            )}
          </div>
          <p
            className={classNames(
              '... max-w-full truncate text-xs text-grey-40'
            )}
          >
            {secondaryText}
          </p>
        </div>
        {heroElement ? (
          <div className='ml-auto flex items-center gap-1'>
            {warning && <SvgWarningIcon className='mr-1 self-end pb-0.5' />}
            <div
              className={classNames(
                'flex flex-col justify-start pr-1 text-right leading-tight'
              )}
            >
              {heroElement}
            </div>
            <div className='flex gap-2'>
              {user && (
                <Avatar
                  image={user.image ?? ''}
                  name={user.name}
                  size='small'
                />
              )}
              {status && <JobStatusBadge small={true} value={status} />}
              {showNewTabIcon && (
                <div className='opacity-0 transition-opacity duration-200 group-hover:opacity-100'>
                  <IconButton
                    icon={faUpRightFromSquare}
                    onClick={handleNewTabClick}
                    size='xs'
                    ariaLabel='Open in new tab'
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className='ml-auto flex items-center gap-2'>
            <div className='pr-1'>
              <p
                className={classNames('whitespace-nowrap text-sm font-medium')}
              >
                <span className={classNames()}>{hero?.number}</span>{' '}
                {hero?.directive}
              </p>
            </div>
            {showNewTabIcon && (
              <div className='opacity-0 transition-opacity duration-200 group-hover:opacity-100'>
                <IconButton
                  icon={faUpRightFromSquare}
                  onClick={handleNewTabClick}
                  size='xs'
                  ariaLabel='Open in new tab'
                />
              </div>
            )}
          </div>
        )}
        {deletable && onClick && (
          <button onClick={onClick}>
            <FontAwesomeIcon
              icon={faTrashCan}
              className='-mb-1 h-6 w-6 stroke-1 text-red-600 opacity-50 hover:opacity-100'
            />
          </button>
        )}
      </Link>
    </div>
  );
}

export function CardSmallStatic({
  image,
  showImage = true,
  titleText,
  deletable,
  onClick,
  secondaryText,
  user,
  status,
  hero,
  warning,
  heroElement,
  isActive,
  isHighlighted,
}: CardSmallStaticProps) {
  return (
    <div
      className={classNames(
        'mb-2 flex w-auto flex-1 items-center gap-2 truncate rounded border px-2.5 py-2 shadow-sm transition-all duration-300',
        { 'cursor-pointer hover:shadow': onClick },
        { 'bg-yellow-10': warning },
        { 'bg-white': !warning && !isActive },
        'mx-3 mb-2 flex items-center gap-2 rounded px-2.5 shadow-sm',
        // Active state styling
        {
          'border-brand bg-brand-ghost shadow-md': isActive,
          'border-grey-10': !isActive && !isHighlighted,
          'border-blue-500 ring-blue-200 border-2 ring-2': isHighlighted,
        }
      )}
      onClick={onClick}
    >
      {showImage && (
        <div className='h-11 w-11'>
          <Avatar
            image={image}
            name={titleText}
            size='full'
            square
            className='mr-3 h-11 w-11 rounded-md'
          />
        </div>
      )}
      <div className='ml-1 flex flex-col items-start overflow-hidden text-base '>
        <div className='flex items-center gap-1'>
          <p
            className={classNames(
              '... max-w-full truncate text-base font-medium text-grey-70 lg:text-[15px] lg:leading-5'
            )}
          >
            {titleText}
          </p>
          {status === 'Inactive' && (
            <div
              className='h-2 w-2 flex-shrink-0 rounded-full bg-red-500'
              title='Inactive'
              aria-label='Inactive status'
            />
          )}
        </div>
        <p
          className={classNames('... max-w-full truncate text-xs text-grey-40')}
        >
          {secondaryText}
        </p>
      </div>
      {heroElement ? (
        <div className='ml-auto flex items-center gap-1'>
          {warning && <SvgWarningIcon className='mr-1 self-end pb-0.5' />}
          <div
            className={classNames(
              'flex flex-col justify-start pr-1 text-right leading-tight'
            )}
          >
            {heroElement}
          </div>
          <div className='flex gap-2'>
            {user && (
              <Avatar image={user.image ?? ''} name={user.name} size='small' />
            )}
            {status && <JobStatusBadge small={true} value={status} />}
          </div>
        </div>
      ) : (
        <div className='ml-auto pr-1'>
          <p className={classNames('whitespace-nowrap text-sm font-medium')}>
            <span>{hero?.number}</span> {hero?.directive}
          </p>
        </div>
      )}
      {deletable && onClick && (
        <button onClick={onClick}>
          <FontAwesomeIcon
            icon={faTrashCan}
            className='-mb-1 h-6 w-6 stroke-1 text-red-600 opacity-50 hover:opacity-100'
          />
        </button>
      )}
    </div>
  );
}
